import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, Loader2, CheckCircle, AlertCircle, MapPin, Car, User, Clock } from 'lucide-react';
import { api } from '../services/api';

const CATEGORIES = [
  { key: 'punctuality', label: 'Pünktlichkeit', labelEn: 'Punctuality' },
  { key: 'cleanliness', label: 'Sauberkeit', labelEn: 'Cleanliness' },
  { key: 'friendliness', label: 'Freundlichkeit', labelEn: 'Friendliness' },
  { key: 'driving_style', label: 'Fahrstil', labelEn: 'Driving Style' },
];

function StarRating({ value, onChange, size = 32 }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex gap-1" data-testid="star-rating">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star)}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform hover:scale-110 focus:outline-none"
          data-testid={`star-${star}`}
        >
          <Star
            size={size}
            className={`transition-colors ${
              star <= (hovered || value)
                ? 'text-amber-400 fill-amber-400'
                : 'text-slate-200'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function RatePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState('loading'); // loading, form, submitting, success, error
  const [errorMsg, setErrorMsg] = useState('');
  const [transferInfo, setTransferInfo] = useState(null);
  const [ratings, setRatings] = useState({
    punctuality: 0,
    cleanliness: 0,
    friendliness: 0,
    driving_style: 0,
  });
  const [comment, setComment] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('Kein Bewertungstoken gefunden. Bitte verwenden Sie den Link aus Ihrer E-Mail.');
      return;
    }
    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const res = await api.get(`/ratings/verify/${token}`);
      setTransferInfo(res.data);
      setState('form');
    } catch (err) {
      setState('error');
      setErrorMsg(err.response?.data?.detail || 'Ungültiger oder abgelaufener Bewertungslink.');
    }
  };

  const handleSubmit = async () => {
    const allRated = Object.values(ratings).every(v => v > 0);
    if (!allRated) {
      alert('Bitte bewerten Sie alle Kategorien.');
      return;
    }

    setState('submitting');
    try {
      await api.post('/ratings/submit', {
        token,
        ...ratings,
        comment: comment || null,
      });
      setState('success');
    } catch (err) {
      setState('error');
      setErrorMsg(err.response?.data?.detail || 'Fehler beim Senden der Bewertung.');
    }
  };

  const averageRating = () => {
    const vals = Object.values(ratings).filter(v => v > 0);
    if (vals.length === 0) return 0;
    return (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
  };

  // Loading
  if (state === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={48} className="mx-auto text-orange-500 animate-spin mb-4" />
          <p className="text-slate-500">Bewertung wird geladen...</p>
        </div>
      </div>
    );
  }

  // Error
  if (state === 'error') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md w-full">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-bold text-slate-800 mb-2">Fehler</h2>
          <p className="text-slate-500 mb-6">{errorMsg}</p>
          <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors" data-testid="back-home-btn">
            Zur Startseite
          </a>
        </div>
      </div>
    );
  }

  // Success
  if (state === 'success') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center p-8 bg-white rounded-2xl shadow-sm max-w-md w-full" data-testid="rating-success">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Vielen Dank!</h2>
          <p className="text-slate-500 mb-2">Ihre Bewertung wurde erfolgreich übermittelt.</p>
          <div className="flex items-center justify-center gap-1 mb-6">
            {[1, 2, 3, 4, 5].map(s => (
              <Star key={s} size={24} className={s <= Math.round(parseFloat(averageRating())) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
            ))}
            <span className="ml-2 text-lg font-bold text-slate-800">{averageRating()}</span>
          </div>
          <a href="/" className="inline-block bg-orange-500 text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-orange-600 transition-colors" data-testid="back-home-success-btn">
            Zur Startseite
          </a>
        </div>
      </div>
    );
  }

  // Rating Form
  return (
    <div className="min-h-screen bg-slate-50" data-testid="rate-page">
      {/* Header */}
      <nav className="bg-white border-b border-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-baseline gap-0.5">
            <span className="text-2xl font-extrabold text-orange-500">366</span>
            <span className="text-sm font-semibold text-slate-800">Transfer</span>
          </a>
          <span className="text-sm text-slate-500">Fahrerbewertung</span>
        </div>
      </nav>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Driver Info Card */}
        {transferInfo && (
          <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center text-white text-xl font-bold">
                {transferInfo.driver_name?.charAt(0)?.toUpperCase() || 'F'}
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-800" data-testid="driver-name">{transferInfo.driver_name}</h2>
                <p className="text-sm text-slate-500 flex items-center gap-1">
                  <Car size={14} /> {transferInfo.vehicle}
                </p>
              </div>
            </div>

            <div className="bg-slate-50 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-2.5 h-2.5 rounded-full bg-orange-500" />
                  <div className="w-0.5 h-5 bg-slate-300" />
                  <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-700">{transferInfo.pickup}</p>
                  <p className="text-sm text-slate-700 mt-3">{transferInfo.dropoff}</p>
                </div>
              </div>
              {transferInfo.date && (
                <div className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-200 text-xs text-slate-500">
                  <Clock size={12} />
                  {new Date(transferInfo.date).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rating Form */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
          <h3 className="text-lg font-bold text-slate-800 mb-1">Bewerten Sie Ihren Fahrer</h3>
          <p className="text-sm text-slate-500 mb-6">Vergeben Sie 1-5 Sterne pro Kategorie</p>

          <div className="space-y-6">
            {CATEGORIES.map(cat => (
              <div key={cat.key} className="flex items-center justify-between" data-testid={`category-${cat.key}`}>
                <div>
                  <p className="font-medium text-slate-800">{cat.label}</p>
                  <p className="text-xs text-slate-400">{cat.labelEn}</p>
                </div>
                <StarRating
                  value={ratings[cat.key]}
                  onChange={(val) => setRatings(prev => ({ ...prev, [cat.key]: val }))}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Comment */}
        <div className="bg-white rounded-2xl border border-slate-100 p-6 mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Kommentar (optional)</label>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Teilen Sie Ihre Erfahrung..."
            rows={3}
            className="w-full px-4 py-3 bg-slate-50 rounded-xl border border-slate-200 focus:border-orange-500 outline-none text-sm resize-none"
            data-testid="rating-comment"
          />
        </div>

        {/* Average Preview */}
        {Object.values(ratings).some(v => v > 0) && (
          <div className="bg-white rounded-2xl border border-slate-100 p-4 mb-6 text-center">
            <p className="text-sm text-slate-500 mb-1">Gesamtbewertung</p>
            <div className="flex items-center justify-center gap-1">
              {[1, 2, 3, 4, 5].map(s => (
                <Star key={s} size={20} className={s <= Math.round(parseFloat(averageRating())) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'} />
              ))}
              <span className="ml-2 text-2xl font-bold text-slate-800">{averageRating()}</span>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          onClick={handleSubmit}
          disabled={state === 'submitting' || !Object.values(ratings).every(v => v > 0)}
          className="w-full py-4 rounded-xl bg-orange-500 text-white font-semibold text-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          data-testid="submit-rating-btn"
        >
          {state === 'submitting' ? (
            <Loader2 size={20} className="animate-spin" />
          ) : (
            'Bewertung abschicken'
          )}
        </button>
      </div>
    </div>
  );
}
